import { useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { TESTIMONIAL_PAIRS } from "../../data/testimonialData";

function FlipCard({ front, back }) {
  const [flipped, setFlipped] = useState(false);
  const data = flipped ? back : front;

  return (
    <div
      onClick={() => setFlipped((f) => !f)}
      className="cursor-pointer"
      style={{ perspective: 1000 }}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
        style={{ transformStyle: "preserve-3d" }}
        className="relative h-[280px]"
      >
        {/* Front */}
        <CardFace data={front} isFront={true} />
        {/* Back */}
        <CardFace data={back} isFront={false} />
      </motion.div>
    </div>
  );
}

function CardFace({ data, isFront }) {
  return (
    <div
      className="absolute inset-0 rounded-2xl p-7 flex flex-col gap-3 border"
      style={{
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: isFront ? "rotateY(0deg)" : "rotateY(180deg)",
        background: isFront ? "white" : `${data.color}06`,
        borderColor: isFront ? "#f1f5f9" : `${data.color}30`,
        boxShadow: isFront ? "0 4px 24px rgba(0,0,0,0.06)" : `0 8px 32px ${data.color}15`,
      }}
    >
      <div className="absolute top-3.5 right-4 text-[10px] text-slate-400 font-bold flex items-center gap-1 font-body">
        {isFront ? "Tap to flip" : "Flip back"}
      </div>

      <div className="flex gap-1">
        {Array(data.stars).fill(0).map((_, i) => (
          <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
        ))}
      </div>

      <p className="text-slate-600 leading-relaxed text-sm italic flex-1 font-body">
        "{data.text}"
      </p>

      <div className="flex items-center gap-3.5 pt-3.5 border-t" style={{ borderColor: isFront ? "#f1f5f9" : `${data.color}20` }}>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-black text-xs flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${data.color}, ${data.color}cc)` }}
        >
          {data.avatar}
        </div>
        <div>
          <p className="font-extrabold text-sm text-slate-900 font-body">{data.name}</p>
          <p className="text-xs text-slate-500 font-body">
            {data.role} &middot;{" "}
            <span className="font-bold" style={{ color: data.color }}>{data.company}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export function TestimonialsSection() {
  return (
    <section data-testid="testimonials-section" className="py-14 md:py-18 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="text-red-600 font-bold text-xs tracking-[0.2em] uppercase font-body">
            Client Stories
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[44px] font-black mt-3 mb-3 tracking-tight">
            What Our Clients Say
          </h2>
          <p className="text-slate-500 text-base md:text-lg font-body">
            Real stories — tap each card to reveal another perspective
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {TESTIMONIAL_PAIRS.map((pair, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <FlipCard front={pair.front} back={pair.back} />
            </motion.div>
          ))}
        </div>

        <p className="text-center text-slate-400 text-[13px] mt-5 font-semibold font-body">
          Tap any card to read another client's story
        </p>
      </div>
    </section>
  );
}
